import type { ReferencePeptide } from "@/lib/research/types";

/**
 * Language-neutral reference data for all 27 research peptides.
 * Translatable prose (summary, mechanism, safety) lives in i18n messages.
 * Study relationships live in src/lib/research/studies.ts via ResearchStudy.peptideSlugs.
 */
export const REFERENCE_PEPTIDES: ReferencePeptide[] = [
	{
		code: "EL-01",
		slug: "ghk-cu",
		name: "GHK-Cu",
		aliases: ["Copper Tripeptide-1", "Kupfer-Tripeptid"],
		classification: "Kupfer-Tripeptid-Komplex",
		sequence: "Gly-His-Lys · Cu²⁺",
		cas: "49557-75-7",
		mw: "340.39 Da (Apopeptid) · 403.92 Da (Cu-Komplex)",
		inci: "Copper Tripeptide-1",
		origin: "Synthese · Basel (CH)",
		status: "available",
		applicationFieldIds: ["cosmetic", "wound-care"],
	},
	{
		code: "EL-02",
		slug: "retatrutide",
		name: "Retatrutide",
		aliases: ["LY3437943"],
		classification: "GLP-1 / GIP / Glucagon Triagonist",
		sequence: "Synthetisches 39-AS-Peptid (proprietär)",
		mw: "≈4.91 kDa",
		origin: "Synthese · Basel (CH)",
		status: "restricted",
		applicationFieldIds: ["metabolic"],
		report: {
			lab: "Janoshik Analytical",
			purity: "99,588 %",
			content: "10,92 mg",
			declared: "10 mg",
			date: "11. Mai 2026",
			taskId: "147356",
			verifyKey: "S15GPICWV71H",
			reportUrl: "/reports/janoshik-retatrutide.png",
		},
	},
	{
		code: "EL-03",
		slug: "epithalon",
		name: "Epithalon",
		aliases: ["Epitalon", "AEDG"],
		classification: "Tetrapeptid · Khavinson-Reihe",
		sequence: "Ala-Glu-Asp-Gly",
		cas: "307297-39-8",
		mw: "390.35 Da",
		origin: "Synthese · München (DE)",
		status: "available",
		applicationFieldIds: ["longevity"],
	},
	{
		code: "EL-04",
		slug: "mots-c",
		name: "MOTS-c",
		classification: "Mitochondrial-Derived Peptide (16 AS)",
		sequence: "MRWQEMGYIFYPRKLR",
		mw: "≈2.17 kDa",
		origin: "Synthese · Zürich (CH)",
		status: "available",
		applicationFieldIds: ["metabolic", "wellness-sports"],
	},
	{
		code: "EL-05",
		slug: "sermorelin-acetate",
		name: "Sermorelin Acetate",
		aliases: ["GHRH(1-29)"],
		classification: "GHRH-Analog (Wachstumshormon-Sekretagog)",
		sequence: "GHRH(1-29) · Acetat-Salz",
		cas: "114466-38-5",
		mw: "3.358 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		// Source: "Pharma-Adjacent" (omitted — no matching field), "Endokrine Forschung" → wellness-sports
		applicationFieldIds: ["wellness-sports"],
	},
	{
		code: "EL-06",
		slug: "mt-1",
		name: "MT-1",
		aliases: ["Afamelanotide", "Melanotan-1"],
		classification: "α-MSH-Analog (zyklisch)",
		sequence: "[Nle⁴, D-Phe⁷]-α-MSH",
		cas: "75921-69-6",
		mw: "1.647 kDa",
		origin: "Synthese · Basel (CH)",
		status: "restricted",
		// Source: "Pharma-Adjacent" (omitted), "Photoprotection Research" → cosmetic (skin/UV protection)
		applicationFieldIds: ["cosmetic"],
	},
	{
		code: "EL-07",
		slug: "mt-2",
		name: "MT-2",
		aliases: ["Melanotan 2 Acetate"],
		classification: "Zyklisches Melanocortin-Analog",
		sequence: "Ac-Nle-cyclo(-Asp-His-D-Phe-Arg-Trp-Lys-)-NH₂",
		cas: "121062-08-6",
		mw: "1.024 kDa",
		origin: "Synthese · Basel (CH)",
		status: "restricted",
		// Source: "Receptor Research" — no matching field among the 6; all omitted
		applicationFieldIds: [],
	},
	{
		code: "EL-08",
		slug: "bpc-157",
		name: "BPC-157",
		aliases: ["Body Protection Compound 157"],
		classification: "Pentadeca-Peptid (gastrisches Fragment)",
		sequence: "Gly-Glu-Pro-Pro-Pro-Gly-Lys-Pro-Ala-Asp-Asp-Ala-Gly-Leu-Val",
		cas: "137525-51-0",
		mw: "1.419 kDa",
		origin: "Synthese · Zürich (CH)",
		status: "available",
		// Source: "Wound Care", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["wound-care"],
	},
	{
		code: "EL-09",
		slug: "tb-500",
		name: "TB-500",
		aliases: ["Thymosin Beta-4 (Vollsequenz oder LKKTETQ-Fragment)"],
		classification: "Aktin-bindendes Peptid",
		sequence: "Ac-SDKPDMAEIEKFDKSKLKKTETQEKNPLPSKETIEQEKQAGES",
		cas: "77591-33-4",
		mw: "4.963 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		// Source: "Wound Care", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["wound-care"],
	},
	{
		code: "EL-10",
		slug: "pnc-27",
		name: "PNC-27",
		classification: "HDM-2-bindendes Membran-Residenz-Peptid",
		sequence: "PPLSQETFSDLWKLLKKWKMRRNQFWVKVQRG",
		mw: "≈4.0 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "pipeline",
		// Source: "Oncology Research" — no matching field among the 6; all omitted
		applicationFieldIds: [],
	},
	{
		code: "EL-11",
		slug: "slu-pp-332",
		name: "SLU-PP-332",
		classification: "Small Molecule · ERR-α/β/γ Pan-Agonist",
		mw: "≈365 Da",
		origin: "Synthese · München (DE)",
		status: "pipeline",
		applicationFieldIds: ["metabolic", "wellness-sports"],
		isSmallMolecule: true,
	},
	{
		code: "EL-12",
		slug: "thymosin-alpha-1",
		name: "Thymosin Alpha-1",
		aliases: ["Thymalfasin", "Zadaxin"],
		classification: "Immunmodulatorisches 28-AS-Peptid",
		sequence: "Ac-SDAAVDTSSEITTKDLKEKKEVVEEAEN",
		cas: "62304-98-7",
		mw: "3.108 kDa",
		origin: "Synthese · Basel (CH)",
		status: "available",
		// Source: "Pharma-Adjacent" (omitted), "Immune Research" — no matching field; all omitted
		applicationFieldIds: [],
	},
	{
		code: "EL-13",
		slug: "ss-31",
		name: "SS-31",
		aliases: ["Elamipretide", "Bendavia"],
		classification: "Mitochondrien-zielendes Tetrapeptid",
		sequence: "D-Arg-Dmt-Lys-Phe-NH₂",
		cas: "736992-21-5",
		mw: "639.79 Da",
		origin: "Synthese · Zürich (CH)",
		status: "restricted",
		// Source: "Pharma-Adjacent" (omitted), "Mitochondrial Research" → metabolic
		applicationFieldIds: ["metabolic"],
	},
	{
		code: "EL-14",
		slug: "glutathione",
		name: "Glutathione",
		aliases: ["GSH", "γ-Glutamyl-Cysteinyl-Glycin"],
		classification: "Tripeptid · zentraler Antioxidans-Cofaktor",
		sequence: "γ-Glu-Cys-Gly",
		cas: "70-18-8",
		mw: "307.32 Da",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		// Source: "Cosmetic", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["cosmetic"],
	},
	{
		code: "EL-15",
		slug: "snap-8",
		name: "Snap-8",
		aliases: ["Acetyl Octapeptide-3"],
		classification: "SNARE-Komplex-Modulator (Octapeptid)",
		sequence: "Ac-Glu-Glu-Met-Gln-Arg-Arg-Ala-Asp-NH₂",
		cas: "868844-74-0",
		mw: "894.97 Da",
		inci: "Acetyl Octapeptide-3",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		applicationFieldIds: ["cosmetic"],
	},
	{
		code: "EL-16",
		slug: "kpv",
		name: "KPV",
		aliases: ["Lysine-Proline-Valine", "α-MSH(11-13)"],
		classification: "C-terminales α-MSH-Tripeptid",
		sequence: "Lys-Pro-Val",
		mw: "342.48 Da",
		origin: "Synthese · München (DE)",
		status: "available",
		// Source: "Wound Care", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["wound-care"],
	},
	{
		code: "EL-17",
		slug: "semax",
		name: "Semax",
		classification: "Heptapeptid · ACTH(4-7)-Analog",
		sequence: "Met-Glu-His-Phe-Pro-Gly-Pro",
		cas: "80714-61-0",
		mw: "813.92 Da",
		origin: "Synthese · München (DE)",
		status: "available",
		// Source: "CNS Research", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["cns"],
	},
	{
		code: "EL-18",
		slug: "selank",
		name: "Selank",
		classification: "Heptapeptid · Tuftsin-Analog",
		sequence: "Thr-Lys-Pro-Arg-Pro-Gly-Pro",
		cas: "129954-34-3",
		mw: "751.89 Da",
		origin: "Synthese · München (DE)",
		status: "available",
		applicationFieldIds: ["cns"],
	},
	{
		code: "EL-19",
		slug: "dsip",
		name: "DSIP",
		aliases: ["Delta Sleep-Inducing Peptide"],
		classification: "Neuromodulatorisches 9-AS-Peptid",
		sequence: "Trp-Ala-Gly-Gly-Asp-Ala-Ser-Gly-Glu",
		cas: "62568-57-4",
		mw: "848.81 Da",
		origin: "Synthese · Zürich (CH)",
		status: "available",
		applicationFieldIds: ["cns"],
	},
	{
		code: "EL-20",
		slug: "ll-37",
		name: "LL-37",
		aliases: ["Cathelicidin", "hCAP18"],
		classification: "Antimikrobielles 37-AS-Peptid",
		sequence: "LLGDFFRKSKEKIGKEFKRIVQRIKDFLRNLVPRTES",
		mw: "4.493 kDa",
		origin: "Synthese · Basel (CH)",
		status: "available",
		// Source: "Wound Care", "Pharma-Adjacent" (omitted)
		applicationFieldIds: ["wound-care"],
	},
	{
		code: "EL-21",
		slug: "pinealon",
		name: "Pinealon",
		classification: "Tripeptid · Khavinson-Reihe",
		sequence: "Glu-Asp-Arg",
		mw: "418.40 Da",
		origin: "Synthese · München (DE)",
		status: "available",
		applicationFieldIds: ["cns", "longevity"],
	},
	{
		code: "EL-22",
		slug: "adipotide",
		name: "Adipotide",
		aliases: ["FTPP", "CKGGRAKDC-GG-D(KLAKLAK)₂"],
		classification: "Pro-apoptotisches Targeting-Peptid",
		sequence: "CKGGRAKDC-GG-D(KLAKLAK)₂",
		mw: "≈2.6 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "pipeline",
		// Source: "Oncology Research" (no matching field, omitted), "Metabolic Research" → metabolic
		applicationFieldIds: ["metabolic"],
	},
	{
		code: "EL-23",
		slug: "foxo4-dri",
		name: "FOXO4-DRI",
		aliases: ["FOXO4-D-Retro-Inverso"],
		classification: "Senolytisches D-Aminosäure-Peptid",
		sequence: "ltlrkepaseiaqsileay (D-AS-Retroinverso)",
		mw: "≈2.40 kDa",
		origin: "Synthese · Zürich (CH)",
		status: "pipeline",
		// Source: "Longevity Research", "Oncology Research" (no matching field, omitted)
		applicationFieldIds: ["longevity"],
	},
	{
		code: "EL-25",
		slug: "cjc-1295",
		name: "CJC-1295",
		// No-DAC form (Mod GRF 1-29), as supplied and as listed by the reference retailer; CAS
		// 863288-34-0 belongs to the DAC-conjugated molecule and is therefore not stated here.
		aliases: ["Mod GRF 1-29", "CJC-1295 No DAC", "Modified GRF (1-29)"],
		classification: "GHRH-Analog · Mod GRF 1-29 (ohne DAC)",
		sequence:
			"Tyr-D-Ala-Asp-Ala-Ile-Phe-Thr-Gln-Ser-Tyr-Arg-Lys-Val-Leu-Ala-Gln-Leu-Ser-Ala-Arg-Lys-Leu-Leu-Gln-Asp-Ile-Leu-Ser-Arg-NH2",
		mw: "3.368 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		// Source: "Pharma-Adjacent" (omitted), "Endokrine Forschung" → wellness-sports
		applicationFieldIds: ["wellness-sports"],
	},
	{
		code: "EL-26",
		slug: "ipamorelin",
		name: "Ipamorelin",
		aliases: ["NN703"],
		classification: "Selektiver GHS-R1a-Agonist (Pentapeptid)",
		sequence: "Aib-His-D-2-Nal-D-Phe-Lys-NH₂",
		cas: "170851-70-4",
		mw: "711.86 Da",
		origin: "Synthese · Basel (CH)",
		status: "available",
		// Source: "Pharma-Adjacent" (omitted), "Endokrine Forschung" → wellness-sports
		applicationFieldIds: ["wellness-sports"],
	},
	{
		code: "EL-27",
		slug: "semaglutide",
		name: "Semaglutide",
		aliases: ["Ozempic", "Wegovy", "Rybelsus"],
		classification: "GLP-1-Analog · Lipid-Acyl-modifiziert",
		sequence:
			"His-Aib-Glu-Gly-Thr-Phe-Thr-Ser-Asp-Val-Ser-Ser-Tyr-Leu-Glu-Gly-Gln-Ala-Ala-Lys[γGlu-(OEG)₂-C18diC]-Glu-Phe-Ile-Ala-Trp-Leu-Val-Arg-Gly-Arg-Gly",
		cas: "910463-68-2",
		mw: "4.114 kDa",
		origin: "Synthese · Zürich (CH)",
		status: "restricted",
		// Source: "Pharma-Adjacent" (omitted), "Metabolic Research" → metabolic
		applicationFieldIds: ["metabolic"],
	},
	{
		code: "EL-24",
		slug: "tesamorelin",
		name: "Tesamorelin",
		aliases: ["TH9507", "Egrifta"],
		classification: "GHRH-Analog · 44-AS-Peptid",
		sequence: "[Trans-3-hexenoyl]-GHRH(1-44)-NH₂",
		cas: "218949-48-5",
		mw: "5.196 kDa",
		origin: "Synthese · Heidelberg (DE)",
		status: "available",
		// Source: "Pharma-Adjacent" (omitted), "Endokrine Forschung" → wellness-sports
		applicationFieldIds: ["wellness-sports"],
	},
];

export function getReferencePeptideBySlug(slug: string): ReferencePeptide | undefined {
	return REFERENCE_PEPTIDES.find((p) => p.slug === slug);
}
